/**
 * Product naming — one place that decides what a thing is called in the UI.
 *
 * The platform is presented white-label: a person using Quodin sees capabilities
 * ("AI Gateway", "Cluster Console"), not the projects that implement them
 * ("OmniRoute", "Headlamp"). That keeps the surface stable when a component is
 * replaced, and keeps the vendor mix out of screenshots and demos.
 *
 * Where the real name is deliberately kept:
 *
 *   - Comments, config keys, plugin IDs, service and namespace names. Someone
 *     debugging at 3am needs to grep for the thing that is actually running.
 *   - RUNBOOK.md and the operational docs, for the same reason.
 *   - Any error message naming a component an operator must go and fix.
 *
 * White-labelling the operator's view would not be branding, it would be
 * making the platform harder to run. The line is: presentation is branded,
 * operations are literal.
 */

export type ProductKey =
  | 'gateway'
  | 'tracing'
  | 'automation'
  | 'messaging'
  | 'dashboards'
  | 'clusterConsole'
  | 'infraMonitor'
  | 'costEngine'
  | 'metrics'
  | 'vectorStore'
  | 'delivery'
  | 'provisioning'
  | 'pipelines'
  | 'flowBuilder';

type Product = {
  /** What the UI calls it. */
  name: string;
  /** One line describing the capability, not the implementation. */
  blurb: string;
  /**
   * The project behind it. Present so this file doubles as the mapping an
   * operator needs, and so a page can disclose it where that is genuinely
   * useful — a "powered by" line rather than a product name.
   */
  upstream: string;
};

export const PRODUCTS: Record<ProductKey, Product> = {
  gateway: {
    name: 'AI Gateway',
    blurb: 'One endpoint in front of every model provider, with per-consumer keys and budgets.',
    // A whitelabelled build, made and deployed by infrastructure/omniroute.
    upstream: 'OmniRoute',
  },
  tracing: {
    name: 'AI Tracing',
    blurb: 'Traces, prompt management and evaluations for everything the models do.',
    upstream: 'Langfuse',
  },
  automation: {
    name: 'Automation',
    blurb: 'Visual workflows — chain APIs, models and webhooks without writing a service.',
    upstream: 'n8n',
  },
  messaging: {
    name: 'Messaging',
    blurb: 'WhatsApp gateway — send and receive messages programmatically.',
    upstream: 'Evolution API',
  },
  dashboards: {
    name: 'Dashboards',
    blurb: 'Cluster and application dashboards. Signs in with the same Quode account.',
    upstream: 'Grafana',
  },
  clusterConsole: {
    name: 'Cluster Console',
    blurb:
      'Kubernetes UI. What you can see is decided by your own Azure role, not a shared account.',
    upstream: 'Headlamp',
  },
  infraMonitor: {
    name: 'Infrastructure Monitor',
    blurb: 'Agent and SNMP monitoring for hosts outside the cluster.',
    upstream: 'Zabbix',
  },
  costEngine: {
    name: 'Cost Engine',
    blurb: 'Kubernetes cost allocation, priced from a list-price model.',
    upstream: 'OpenCost',
  },
  metrics: {
    name: 'Metrics',
    blurb: 'Time-series metrics and alerting for the platform.',
    upstream: 'Prometheus',
  },
  vectorStore: {
    name: 'Vector Store',
    blurb: 'Embeddings for the memory and skill store.',
    upstream: 'pgvector',
  },
  delivery: {
    name: 'Delivery',
    blurb: 'Continuous delivery — git is the source of truth for what runs.',
    upstream: 'Argo CD',
  },
  provisioning: {
    name: 'Provisioning',
    blurb: 'Cloud resources declared as Kubernetes objects.',
    upstream: 'Crossplane',
  },
  flowBuilder: {
    name: 'Flow Builder',
    blurb:
      'Build and run model flows visually. Calls go through the AI Gateway, so a flow inherits its budget and audit.',
    upstream: 'Langflow',
  },
  pipelines: {
    name: 'Pipelines',
    blurb: 'Container-native workflow runs.',
    upstream: 'Argo Workflows',
  },
};

/** The display name for a product. */
export const productName = (k: ProductKey): string => PRODUCTS[k].name;

/**
 * "AI Gateway (OmniRoute)" — for the few places where naming the project is more
 * helpful than hiding it, such as a panel an operator uses to diagnose.
 */
export const productWithUpstream = (k: ProductKey): string =>
  `${PRODUCTS[k].name} (${PRODUCTS[k].upstream})`;

/** Every product, for a settings or about page that lists what is running. */
export const allProducts = (): (Product & { key: ProductKey })[] =>
  (Object.keys(PRODUCTS) as ProductKey[]).map(k => ({ key: k, ...PRODUCTS[k] }));
